/**
 * BLK PACKS — IP / Software Disclaimer chip.
 *
 * Tiny inline component dropped at the bottom of high-risk surfaces
 * (AR launch screen, drop detail page, checkout modal, share-card
 * footer) to make the transformative-software / unaffiliated posture
 * explicit at the point of user action.
 *
 * Two sizes:
 *  - default  — single line, neutral gray, click-through to /licensing
 *  - inline   — even smaller, used inside dense surfaces
 *  - card     — bordered card for AR launch screens
 *
 * No images, no animations — just text. Avoids visual noise so it
 * doesn't interfere with the Top Shot Moment Detail look.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Info } from "lucide-react";

export const IPDisclaimer = ({ variant = "default", className = "" }) => {
  if (variant === "card") {
    return (
      <div
        className={`rounded-lg border border-white/10 bg-black/40 px-3 py-2.5 text-[10px] leading-[1.45] text-white/55 ${className}`}
        data-testid="ip-disclaimer-card"
      >
        <p className="flex items-start gap-2">
          <Info size={11} className="text-white/45 mt-[1px] flex-shrink-0" strokeWidth={2.4} />
          <span>
            BLK PACKS™ is interactive AR companion software. We do not
            issue or license third-party athlete, team, or league IP.
            Any third-party marks visible on a user-scanned card belong
            to their respective owners and are referenced nominatively to
            identify your own collectible. AR scenes are original,
            transformative software-driven works.{" "}
            <Link
              to="/licensing"
              className="text-amber-300 underline-offset-2 hover:underline"
              data-testid="ip-disclaimer-card-link"
            >
              Learn more
            </Link>
          </span>
        </p>
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <p
        className={`text-[10px] leading-snug text-white/40 ${className}`}
        data-testid="ip-disclaimer-inline"
      >
        BLK PACKS™ is entertainment software. All third-party marks belong
        to their respective owners.{" "}
        <Link to="/licensing" className="text-amber-300/80 hover:underline">
          Licensing
        </Link>
      </p>
    );
  }

  return (
    <p
      className={`text-[11px] leading-snug text-white/50 ${className}`}
      data-testid="ip-disclaimer-default"
    >
      BLK PACKS™ is interactive AR companion software. We do not claim or
      license third-party athlete, team, or league IP. All third-party
      marks belong to their respective owners.{" "}
      <Link
        to="/licensing"
        className="text-amber-300/85 hover:underline"
        data-testid="ip-disclaimer-link"
      >
        Licensing &amp; attribution
      </Link>
      .
    </p>
  );
};

export default IPDisclaimer;
